import {v4 as uuidv4} from 'uuid';

import refreshNoteLinkedGroupActionFunction from 'notebook/notes/groups/action-functions/refreshNoteLinkedGroupActionFunction';
import {
  CreateNoteLinkedGroupProps,
  GroupActionFunctions,
  NoteLinkedGroup,
  NoteLinkedGroupAdapter,
  NoteLinkedGroupKind,
} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {SamplesSource} from 'notebook/sample-propagation/sample-collection/SamplesSourceCollector';
import {isStructuredTable} from 'notebook/TypePredicates';
import {
  AnyStructuredTable,
  isNotePartWithItem,
  NoteLinkedObjectType,
  NotePart,
  NotePartWithItem,
} from 'notebook/util/NoteUtilConstants';
import {Schema} from 'schemas/SchemaConstants';
import {MaybePromise} from 'util/Promises';

import {reduceGroupUpdateNotePart} from './util/ContainedNotePartUtil';

export default abstract class GenericNoteLinkedGroupAdapter<TGroupMetadata, TCreateGroupExtras>
  implements NoteLinkedGroupAdapter<TGroupMetadata, TCreateGroupExtras>
{
  kind: NoteLinkedGroupKind;

  constructor(kind: NoteLinkedGroupKind) {
    this.kind = kind;
  }

  abstract getDisplayName(noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>): string;

  getActionFunctions(): GroupActionFunctions<TGroupMetadata, TCreateGroupExtras> {
    return {
      secondary: [refreshNoteLinkedGroupActionFunction],
      overflow: [],
    };
  }

  // This method is final, and should not be overridden by subclasses.
  createGroup(createProps: CreateNoteLinkedGroupProps<TGroupMetadata, TCreateGroupExtras>): Omit<
    NoteLinkedGroup<TGroupMetadata>,
    'cid' | 'containedNoteParts'
  > & {
    containedNoteParts: Array<Omit<NotePart, 'cid'>>;
  } {
    const {name, kind, groupMetadata, isConfig} = createProps;

    return {
      name,
      type: NoteLinkedObjectType.NOTE_LINKED_GROUP,
      kind,
      provenanceUuid: uuidv4(),
      isConfig: isConfig ?? false,
      groupMetadata,
      containedNoteParts: this.createNoteParts(createProps),
    };
  }

  abstract createNoteParts(
    createProps: CreateNoteLinkedGroupProps<TGroupMetadata, TCreateGroupExtras>
  ): Array<Omit<NotePart, 'cid'>>;

  /**
   * Apply all updates to the group given an updated note part.
   *
   * By default, this always replaces the pre-existing notePart with the updatedNotePart.
   *
   * If your group has more complex logic (conditional, async, one note part affecting another), you can
   * override this method. You'll likely want to call reduceGroupUpdateNotePart to still merge in the change
   * like is done in the default case.
   *
   */
  reduceGroup(
    noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>,
    updatedNotePart: NotePart
  ): MaybePromise<NoteLinkedGroup<TGroupMetadata>> {
    return reduceGroupUpdateNotePart(noteLinkedGroup, updatedNotePart);
  }

  getContainedStructuredTables(noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>): Array<AnyStructuredTable> {
    const notePartsWithStructuredTables = noteLinkedGroup.containedNoteParts.filter(
      (notePart) => isNotePartWithItem(notePart) && isStructuredTable(notePart.item)
    ) as Array<NotePartWithItem<AnyStructuredTable>>;
    return notePartsWithStructuredTables.map((notePart) => notePart.item);
  }

  getGroupSampleSources(noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>): Array<SamplesSource> {
    return [];
  }

  abstract getGroupSchemas(noteLinkedGroup: NoteLinkedGroup<TGroupMetadata>): Array<Schema>;
}
